import React from "react";

// Eight-pointed Star of Ishtar (Sumerian) — pure SVG.
export const StarOfIshtar = ({
  size = 120,
  color = "#C9A84C",
  glow = "#E8C97A",
  opacity = 1,
  className = "",
  withInnerCircle = true,
}) => {
  const c = size / 2;
  const long = size / 2;
  const short = size / 6.5;
  // Build 8 points alternating long/short
  const points = [];
  for (let i = 0; i < 16; i++) {
    const angle = (Math.PI / 8) * i - Math.PI / 2;
    const r = i % 2 === 0 ? long : short;
    points.push(`${c + r * Math.cos(angle)},${c + r * Math.sin(angle)}`);
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      style={{ opacity }}
      aria-hidden="true"
      data-testid="star-of-ishtar"
    >
      <defs>
        <radialGradient id="ishtar-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={glow} stopOpacity="0.95" />
          <stop offset="55%" stopColor={color} stopOpacity="0.7" />
          <stop offset="100%" stopColor={color} stopOpacity="0.25" />
        </radialGradient>
      </defs>
      <polygon points={points.join(" ")} fill="url(#ishtar-grad)" stroke={color} strokeWidth="0.6" strokeOpacity="0.9" />
      {withInnerCircle && (
        <circle cx={c} cy={c} r={size / 14} fill="none" stroke={color} strokeWidth="0.8" strokeOpacity="0.7" />
      )}
    </svg>
  );
};

// Minimal star glyph for inline ornaments
export const StarGlyph = ({ size = 14, color = "#C9A84C", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <polygon
      points="12,2 14,10 22,12 14,14 12,22 10,14 2,12 10,10"
      fill={color}
    />
    <polygon
      points="12,5 13,11 19,12 13,13 12,19 11,13 5,12 11,11"
      fill="rgba(0,0,0,0.35)"
    />
  </svg>
);

// Ziggurat step divider
export const ZigguratDivider = ({ width = 240, height = 28, color = "#C9A84C" }) => {
  // Stepped trapezoid path
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      data-testid="ziggurat-divider"
      style={{ display: "block" }}
    >
      <g stroke={color} strokeWidth="1" fill="none" opacity="0.7">
        <path d={`M 0 ${height} L 40 ${height} L 50 ${height - 7} L 90 ${height - 7} L 100 ${height - 14} L ${width - 100} ${height - 14} L ${width - 90} ${height - 7} L ${width - 40} ${height - 7} L ${width - 0} ${height}`} />
      </g>
      <g fill={color} opacity="0.85">
        <circle cx={width / 2} cy={height - 18} r="2.4" />
      </g>
    </svg>
  );
};

// Cuneiform horizontal motif line (decorative)
export const CuneiformMotif = ({ width = 160, color = "#C9A84C", className = "" }) => (
  <svg width={width} height="10" viewBox={`0 0 ${width} 10`} className={className} aria-hidden="true">
    <g fill={color} opacity="0.55">
      {Array.from({ length: Math.floor(width / 14) }).map((_, i) => (
        <g key={`cuneiform-${i}`} transform={`translate(${i * 14},0)`}>
          <polygon points="2,5 5,2 8,5 5,8" />
          <rect x="10" y="4" width="3" height="2" />
        </g>
      ))}
    </g>
  </svg>
);

// Reusable section eyebrow ornament
export const Ornament = ({ centered = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: centered ? "center" : "flex-start",
      gap: "0.75rem",
      marginBottom: "1.5rem",
    }}
  >
    <span style={{ width: 28, height: 1, background: "#C9A84C", opacity: 0.55 }} />
    <StarGlyph size={12} />
    <span style={{ width: 28, height: 1, background: "#C9A84C", opacity: 0.55 }} />
  </div>
);

// ─────────────────────────────────────────────────────────────
//  BEE & HONEY ORNAMENT FAMILY
//  Heraldic, geometric, ancient — keeps with the Star of Ishtar
//  and cuneiform language. The bee is invoked here as the Sumerian
//  symbol of the queen-organized hive: many bodies, one coherence.
// ─────────────────────────────────────────────────────────────

// Heraldic bee glyph — geometric, gold linework. Matches StarGlyph's weight.
export const BeeGlyph = ({ size = 18, color = "#C9A84C", glow = "#E8C97A", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    className={className}
    aria-hidden="true"
    data-testid="bee-glyph"
  >
    <defs>
      <linearGradient id={`bee-grad-${size}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={glow} />
        <stop offset="100%" stopColor={color} />
      </linearGradient>
    </defs>
    {/* Wings — splayed ovals */}
    <g fill={glow} opacity="0.55">
      <ellipse cx="9" cy="10" rx="5" ry="3" transform="rotate(-22 9 10)" />
      <ellipse cx="23" cy="10" rx="5" ry="3" transform="rotate(22 23 10)" />
    </g>
    {/* Body — abdomen with three stripes */}
    <g>
      {/* head */}
      <circle cx="16" cy="9" r="2.6" fill={`url(#bee-grad-${size})`} />
      {/* eyes */}
      <circle cx="14.7" cy="8.6" r="0.55" fill="#0a0806" opacity="0.85" />
      <circle cx="17.3" cy="8.6" r="0.55" fill="#0a0806" opacity="0.85" />
      {/* antennae */}
      <path d="M14.6 6.6 C 13.6 4.5, 12.3 4.0, 11.6 3.6" stroke={color} strokeWidth="0.7" fill="none" strokeLinecap="round" />
      <path d="M17.4 6.6 C 18.4 4.5, 19.7 4.0, 20.4 3.6" stroke={color} strokeWidth="0.7" fill="none" strokeLinecap="round" />
      {/* abdomen oval */}
      <ellipse cx="16" cy="19" rx="4.6" ry="7" fill={`url(#bee-grad-${size})`} />
      {/* stripes (dark bands) */}
      <path d="M11.7 16.5 Q 16 17.4, 20.3 16.5" stroke="#0a0806" strokeWidth="1.1" fill="none" opacity="0.85" />
      <path d="M11.4 19.5 Q 16 20.4, 20.6 19.5" stroke="#0a0806" strokeWidth="1.1" fill="none" opacity="0.85" />
      <path d="M12.3 22.5 Q 16 23.2, 19.7 22.5" stroke="#0a0806" strokeWidth="1.1" fill="none" opacity="0.85" />
      {/* stinger point */}
      <path d="M16 26 L 16 28" stroke={color} strokeWidth="0.8" strokeLinecap="round" />
    </g>
  </svg>
);

// Tileable honeycomb SVG pattern (use as background-image)
// Returns a data-URL string so it can be applied directly via CSS.
export const honeycombDataUrl = ({ color = "#C9A84C", opacity = 0.06, stroke = 0.7 } = {}) => {
  // Hex-grid: two staggered rows of hexagons in a 60x52 tile
  const c = encodeURIComponent(color);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 52' width='60' height='52'>\
<g fill='none' stroke='${color}' stroke-opacity='${opacity}' stroke-width='${stroke}'>\
<polygon points='15,1 29,1 36,13 29,25 15,25 8,13'/>\
<polygon points='45,1 59,1 66,13 59,25 45,25 38,13'/>\
<polygon points='0,27 8,27 15,39 8,51 0,51'/>\
<polygon points='30,27 44,27 51,39 44,51 30,51 23,39'/>\
<polygon points='60,27 66,27 60,51 53,39'/>\
</g></svg>`;
  return `url("data:image/svg+xml;utf8,${svg.replace(/"/g, "'").replace(/\n/g, "")}")`;
};

// Honeycomb-decorated divider — six hexagons in a row, center one filled
export const HexDivider = ({ width = 240, color = "#C9A84C", className = "" }) => {
  const hexes = 7;
  const w = 22;
  const totalW = hexes * w + 12;
  const scale = width / totalW;
  return (
    <svg
      width={width}
      height={28 * scale}
      viewBox={`0 0 ${totalW} 28`}
      className={className}
      aria-hidden="true"
      data-testid="hex-divider"
      style={{ display: "block" }}
    >
      <g fill="none" stroke={color} strokeWidth="0.9" opacity="0.7">
        {Array.from({ length: hexes }).map((_, i) => {
          const cx = 11 + i * w;
          const cy = 14;
          const r = 8;
          const points = [];
          for (let k = 0; k < 6; k++) {
            const a = (Math.PI / 3) * k - Math.PI / 2;
            points.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
          }
          const isCenter = i === Math.floor(hexes / 2);
          return (
            <polygon
              key={`hex-${i}`}
              points={points.join(" ")}
              fill={isCenter ? color : "none"}
              fillOpacity={isCenter ? 0.85 : 0}
              opacity={isCenter ? 1 : 0.55 + 0.08 * (3 - Math.abs(i - Math.floor(hexes / 2)))}
            />
          );
        })}
      </g>
    </svg>
  );
};

// Slow forming honey drop — REAL-looking honey: glossy bulb with highlight
// stripe, thinning strand, deep amber gradient. Wrap in absolute container
// to position; the CSS `.honey-strand` + `.honey-bulb-anim` classes drive
// the slow viscous animation cycle.
export const HoneyDrip = ({
  width = 18,
  height = 220,
  delay = 0,
  className = "",
  style = {},
}) => {
  const W = width + 28;
  const cx = W / 2;
  const animStyle = { animationDelay: `${delay}s` };
  return (
    <div className={`honey-drip-wrap ${className}`} style={{ width: W, height: height + 50, ...style }} aria-hidden="true" data-testid="honey-drip">
      <svg width={W} height={height + 50} viewBox={`0 0 ${W} ${height + 50}`}>
        <defs>
          {/* Strand body — viscous amber column */}
          <linearGradient id="honey-strand-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#3a2410" stopOpacity="0.95" />
            <stop offset="15%"  stopColor="#a06820" stopOpacity="1" />
            <stop offset="42%"  stopColor="#f3c060" stopOpacity="1" />
            <stop offset="50%"  stopColor="#fff0c0" stopOpacity="1" />
            <stop offset="60%"  stopColor="#f3c060" stopOpacity="1" />
            <stop offset="85%"  stopColor="#a06820" stopOpacity="1" />
            <stop offset="100%" stopColor="#3a2410" stopOpacity="0.95" />
          </linearGradient>
          {/* Vertical fade for strand top */}
          <linearGradient id="honey-fade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#000" stopOpacity="0" />
            <stop offset="22%" stopColor="#000" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#000" stopOpacity="1" />
          </linearGradient>
          <mask id="honey-strand-mask">
            <rect x="0" y="0" width={W} height={height} fill="url(#honey-fade)" />
          </mask>
          {/* Glossy bulb — radial */}
          <radialGradient id="honey-bulb-grad" cx="40%" cy="35%" r="65%">
            <stop offset="0%"  stopColor="#ffeec0" stopOpacity="1" />
            <stop offset="22%" stopColor="#ffd270" stopOpacity="1" />
            <stop offset="65%" stopColor="#c98622" stopOpacity="1" />
            <stop offset="100%" stopColor="#3a2410" stopOpacity="1" />
          </radialGradient>
          {/* Highlight specular */}
          <radialGradient id="honey-spec" cx="38%" cy="28%" r="22%">
            <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ---- Strand ---- */}
        <g className="honey-strand" style={animStyle}>
          {/* Tapered honey strand: wider at top, narrows toward the drop */}
          <path
            d={`M ${cx - width / 2} 0
                Q ${cx - width / 2 - 2} ${height * 0.4}, ${cx - width / 3} ${height * 0.75}
                Q ${cx - width / 4} ${height * 0.9},  ${cx} ${height}
                Q ${cx + width / 4} ${height * 0.9},  ${cx + width / 3} ${height * 0.75}
                Q ${cx + width / 2 + 2} ${height * 0.4}, ${cx + width / 2} 0 Z`}
            fill="url(#honey-strand-grad)"
            mask="url(#honey-strand-mask)"
          />
          {/* Inner highlight stripe down the left side of the strand */}
          <path
            d={`M ${cx - width / 4} 4
                Q ${cx - width / 4 - 1} ${height * 0.5}, ${cx - width / 6} ${height * 0.85}`}
            stroke="#fff5d8"
            strokeWidth={Math.max(1, width / 6)}
            strokeOpacity="0.55"
            fill="none"
            strokeLinecap="round"
            mask="url(#honey-strand-mask)"
            className="honey-shimmer-anim"
          />
        </g>

        {/* ---- Forming bulb at base ---- */}
        <g className="honey-bulb-anim" style={animStyle} transform={`translate(${cx} ${height + 6})`}>
          {/* Main honey droplet — teardrop pointing up into strand */}
          <path
            d={`M 0 ${-width * 0.9}
                C ${width * 0.95} ${-width * 0.6}, ${width * 1.1} ${width * 0.55}, 0 ${width * 1.25}
                C ${-width * 1.1} ${width * 0.55}, ${-width * 0.95} ${-width * 0.6}, 0 ${-width * 0.9} Z`}
            fill="url(#honey-bulb-grad)"
          />
          {/* Specular highlight */}
          <ellipse
            cx={-width * 0.25}
            cy={-width * 0.15}
            rx={width * 0.45}
            ry={width * 0.32}
            fill="url(#honey-spec)"
          />
          {/* Rim light along the bottom curve */}
          <path
            d={`M ${-width * 0.7} ${width * 0.55}
                Q 0 ${width * 1.05}, ${width * 0.7} ${width * 0.55}`}
            stroke="#fff5d8"
            strokeWidth={width / 12}
            strokeOpacity="0.7"
            fill="none"
            strokeLinecap="round"
          />
        </g>
      </svg>
    </div>
  );
};

// Animated bee swarm — photographic bee PNGs flying on independent
// curving paths across the parent. Each bee has wing-flutter via a
// gentle hover animation since the bee images are static stills.
export const BeeSwarm = () => (
  <>
    <div className="real-bee b1" aria-hidden="true" data-testid="real-bee-1">
      <img src="/media/bee-1.png" alt="" />
    </div>
    <div className="real-bee b2" aria-hidden="true" data-testid="real-bee-2">
      <img src="/media/bee-2.png" alt="" />
    </div>
    <div className="real-bee b3" aria-hidden="true" data-testid="real-bee-3">
      <img src="/media/bee-3.png" alt="" />
    </div>
  </>
);

// Animated bee — flutters its wings, drifts on a slow path across its parent.
// The motion path itself is defined in CSS (.flying-bee bee-path keyframes).
export const FlyingBee = ({ size = 26, color = "#C9A84C", glow = "#E8C97A", className = "", style = {} }) => (
  <div className={`flying-bee ${className}`} style={style} aria-hidden="true" data-testid="flying-bee">
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
    >
      <defs>
        <linearGradient id={`bee-body-${size}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={glow} />
          <stop offset="50%" stopColor={color} />
          <stop offset="100%" stopColor="#8a6a25" />
        </linearGradient>
        <radialGradient id={`bee-wing-${size}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="60%" stopColor="#ffeec0" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ffeec0" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Wings (animated separately) */}
      <g className="bee-wings">
        <ellipse cx="9" cy="9" rx="6" ry="3.5" transform="rotate(-25 9 9)" fill={`url(#bee-wing-${size})`} />
        <ellipse cx="23" cy="9" rx="6" ry="3.5" transform="rotate(25 23 9)" fill={`url(#bee-wing-${size})`} />
      </g>
      {/* Head */}
      <circle cx="16" cy="9" r="3" fill={`url(#bee-body-${size})`} />
      <circle cx="14.6" cy="8.7" r="0.6" fill="#0a0806" />
      <circle cx="17.4" cy="8.7" r="0.6" fill="#0a0806" />
      {/* Antennae */}
      <path d="M14.6 6.8 C 13.5 4.4, 12.2 3.8, 11.4 3.4" stroke={color} strokeWidth="0.8" fill="none" strokeLinecap="round" />
      <path d="M17.4 6.8 C 18.5 4.4, 19.8 3.8, 20.6 3.4" stroke={color} strokeWidth="0.8" fill="none" strokeLinecap="round" />
      {/* Abdomen with realistic stripes */}
      <ellipse cx="16" cy="20" rx="5" ry="7.5" fill={`url(#bee-body-${size})`} />
      <path d="M11 17 Q 16 18.2, 21 17"   stroke="#0a0806" strokeWidth="1.3" fill="none" />
      <path d="M10.7 20.5 Q 16 21.7, 21.3 20.5" stroke="#0a0806" strokeWidth="1.3" fill="none" />
      <path d="M11.5 24 Q 16 25, 20.5 24"   stroke="#0a0806" strokeWidth="1.3" fill="none" />
      {/* Stinger */}
      <path d="M16 27.5 L 16 29.5" stroke="#3a2410" strokeWidth="0.9" strokeLinecap="round" />
    </svg>
  </div>
);

